/**
 * The full health reading for one channel, in a dialog.
 *
 * It opens from the channel's own mark or name in the status matrix. That is the
 * detail the Console deliberately does not carry: the Console's Channel Status
 * pane is shorthand for a survey author who is sending and does not want to
 * change tabs, and it shows a light and nothing more. Anyone asking why the
 * light is that colour comes here.
 *
 * Nothing is measured in this file. The reading is taken by sam-dbam and stored,
 * so this screen and the Console's pane cannot disagree, and so the answer is
 * there whether or not this screen has been open.
 */

import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import {
    CHANNEL_HEALTH_LABELS,
    channelHealthResult,
    channelHealthTone,
} from '../utils/channelHealthDisplay';

/**
 * Human labels for the mail server's check keys.
 *
 * A key with no label prints itself. A reading that carried a check this screen
 * has never heard of would otherwise show a blank row, and nobody could tell
 * what had been measured.
 */
const CHECK_LABELS = {
    postfix: 'Postfix',
    dovecot: 'Dovecot',
    nginx: 'nginx',
    webhook_daemon_http: 'Webhook daemon',
    nginx_hooks_route: 'Hooks route',
    queue_depth: 'Queue depth',
    deferred_count: 'Deferred',
    last_send_utc: 'Last send (UTC)',
    hours_since_last_send: 'Hours since send',
    spool_disk_pct: 'Spool disk %',
    cert_days_remaining: 'Cert days left',
};

/** Checks whose value is a fault in itself, whatever the overall status says. */
const EXPECTED_VALUES = { nginx_hooks_route: 'intact' };

function isBadCheck(key, value) {
    const expected = EXPECTED_VALUES[key];
    return expected !== undefined && String(value) !== expected;
}

function formatTime(value) {
    if (!value) return null;
    const when = new Date(value);
    return Number.isNaN(when.getTime()) ? null : when.toLocaleString();
}

export default function ChannelHealthReport({ channel, control, onClose }) {
    const closeRef = useRef(null);

    // Escape closes, and focus starts on the close button. Without this the
    // dialog opens with focus still on the row behind it, and a keyboard user
    // has no way out of it.
    useEffect(() => {
        closeRef.current?.focus();
        const onKey = (event) => { if (event.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const result = channelHealthResult(control);
    const evidence = control?.runtime_health_evidence || null;
    const checks = evidence?.checks && typeof evidence.checks === 'object' ? evidence.checks : {};
    const checkEntries = Object.entries(checks);
    const issues = Array.isArray(evidence?.issues) ? evidence.issues : [];
    const checkedAt = formatTime(evidence?.checked_at || control?.runtime_health_checked_at);
    const titleId = `channel-health-report-${channel.id}`;

    return (
        <div className="channel-report__scrim" role="presentation" onClick={onClose}>
            <div
                className="channel-report"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="channel-report__head">
                    <span className="channel-mark" style={{ background: channel.markColor }} aria-hidden="true">
                        {channel.abbr}
                    </span>
                    <span className="channel-report__title" id={titleId}>{channel.name} health</span>
                    <span className={`tag tag--${channelHealthTone(result)}`}>
                        {CHANNEL_HEALTH_LABELS[result]}
                    </span>
                    <button
                        type="button"
                        ref={closeRef}
                        className="channel-report__close"
                        onClick={onClose}
                        aria-label="Close health report"
                    >
                        <X size={16} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </div>

                <div className="channel-report__body">
                    <p className="channel-report__summary">
                        {result === 'off'
                            ? 'Channel is off.'
                            : checkedAt
                                ? `Last test of ${checkedAt} ${result}.`
                                : `No completed channel test is available. ${CHANNEL_HEALTH_LABELS[result]}.`}
                    </p>

                    {result === 'off' && (
                        <p className="channel-report__empty">
                            This channel will not be used for survey delivery until it is turned on.
                        </p>
                    )}

                    {result === 'passed' && (
                        <p className="channel-report__empty">
                            This channel is ready for survey delivery.
                        </p>
                    )}

                    {result === 'failed' && (
                        <p className="channel-report__empty">
                            {control?.runtime_health_reason
                                || 'SamSurveyor has not completed a successful health test for this channel.'}
                        </p>
                    )}

                    {evidence?.error_code && (
                        <p className="channel-report__code">{evidence.error_code}</p>
                    )}

                    {issues.length > 0 && (
                        <ul className="channel-report__issues">
                            {issues.map((issue) => <li key={issue}>{issue}</li>)}
                        </ul>
                    )}

                    {checkEntries.length > 0 && (
                        <dl className="channel-report__checks">
                            {checkEntries.map(([key, value]) => (
                                <div key={key} className="channel-report__check">
                                    <dt>{CHECK_LABELS[key] || key}</dt>
                                    <dd className={isBadCheck(key, value) ? 'is-bad' : undefined}>
                                        {String(value)}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </div>

            </div>
        </div>
    );
}
